import React, { useEffect, useState, useRef } from 'react';
import { Star, TrendingUp, TrendingDown, ArrowUpDown, Search, X, Clock, Zap, AlertCircle } from 'lucide-react';
import { getTheme } from '../App';

// ─── Types ────────────────────────────────────────────────────────────────────

interface DecibelMarket {
  symbol:               string;
  markPrice:            number;
  indexPrice:           number;
  fundingRate:          number;
  predictedFundingRate: number;
  nextFundingTime:      number;  // unix ms
  openInterest:         number;
  openInterestUsd:      number;
  volume24h:            number;
  priceChange24h:       number;
  bestBid:              number;
  bestAsk:              number;
}

interface DecibelPosition {
  symbol:           string;
  side:             string;
  size:             number;
  entryPrice:       number;
  markPrice:        number;
  unrealizedPnl:    number;
  leverage:         number;
  liquidationPrice: number;
  marginUsed:       number;
  roe:              number;
}

interface DecibelOrder {
  orderId:    string;
  symbol:     string;
  side:       string;
  orderType:  string;
  price:      number;
  size:       number;
  filledSize: number;
  status:     string;
  createdAt:  string;
}

interface DecibelAccount {
  equity:        number;
  accountValue:  number;
  margin:        number;
  freeMargin:    number;
  unrealizedPnl: number;
  realizedPnl:   number;
  leverage:      number;
}

interface Props { dark?: boolean; }

// ─── WS URL ───────────────────────────────────────────────────────────────────

const DECIBEL_WS = window.location.port === '3000' || window.location.port === '3001'
  ? `ws://${window.location.hostname}:8883`
  : 'wss://ws.barbarosquant.org/decibel-ws';

const FAVS_KEY = 'decibel_favorites';

type SortKey = 'alpha' | 'volume' | 'change' | 'funding' | 'pfunding' | 'oi';
type SortDir = 'asc' | 'desc';

function loadFavs(): string[] {
  try { return JSON.parse(localStorage.getItem(FAVS_KEY) || '[]'); }
  catch { return []; }
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtPrice(p: number): string {
  if (!p) return '—';
  if (p >= 1000) return p.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (p >= 1)    return p.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 });
  return p.toFixed(6);
}

function fmtFunding(r: number): string {
  return `${r >= 0 ? '+' : ''}${(r * 100).toFixed(4)}%`;
}

function formatCountdown(ms: number): string {
  if (ms <= 0) return '—';
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}s ${m}d`;
  return `${m}d ${s.toString().padStart(2, '0')}s`;
}

function shortSymbol(sym: string): string {
  // 'BTC-USD' → 'BTC'
  return sym.split('-')[0] || sym;
}

// ─── Order Modal ──────────────────────────────────────────────────────────────

interface OrderModalProps {
  market: DecibelMarket;
  dark: boolean;
  onClose: () => void;
  onSubmit: (order: any) => void;
  busy: boolean;
}

function OrderModal({ market, dark, onClose, onSubmit, busy }: OrderModalProps) {
  const t = getTheme(dark);
  const [side,      setSide]      = useState<'LONG' | 'SHORT'>('LONG');
  const [orderType, setOrderType] = useState<'MARKET' | 'LIMIT'>('MARKET');
  const [size,      setSize]      = useState('');
  const [price,     setPrice]     = useState('');

  const isValid = parseFloat(size) > 0 && (orderType === 'MARKET' || parseFloat(price) > 0);

  const handleSubmit = () => {
    if (!isValid) return;
    onSubmit({
      symbol:     market.symbol,
      side,
      order_type: orderType,
      size:       parseFloat(size),
      price:      orderType === 'LIMIT' ? parseFloat(price) : undefined,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className={`w-[380px] rounded-2xl border ${t.cardBorder} ${t.card} p-6 shadow-2xl`}>

        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className={`text-base font-bold ${t.gold}`}>{market.symbol}</h3>
            <p className={`text-xs ${t.muted} mt-0.5`}>
              Mark: <span className="text-white font-mono">${fmtPrice(market.markPrice)}</span>
            </p>
          </div>
          <button onClick={onClose} className={`${t.muted} hover:text-white transition-colors`}>
            <X size={18} />
          </button>
        </div>

        {/* Side */}
        <div className="flex gap-2 mb-4">
          {(['LONG', 'SHORT'] as const).map(s => (
            <button
              key={s}
              onClick={() => setSide(s)}
              className={`flex-1 py-2 rounded-lg text-sm font-bold transition-colors ${
                side === s
                  ? s === 'LONG'
                    ? 'bg-emerald-600 text-white'
                    : 'bg-rose-600 text-white'
                  : `${t.btnSecondary} opacity-50`
              }`}
            >
              {s === 'LONG' ? '▲ Long' : '▼ Short'}
            </button>
          ))}
        </div>

        {/* Order type */}
        <div className="flex gap-2 mb-4">
          {(['MARKET', 'LIMIT'] as const).map(ot => (
            <button
              key={ot}
              onClick={() => setOrderType(ot)}
              className={`flex-1 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                orderType === ot
                  ? 'bg-[#4A9FE8] text-[#061530]'
                  : `${t.btnSecondary} opacity-60`
              }`}
            >
              {ot}
            </button>
          ))}
        </div>

        {/* Price (limit only) */}
        {orderType === 'LIMIT' && (
          <div className="mb-3">
            <label className={`text-xs ${t.muted} mb-1 block`}>Fiyat (USD)</label>
            <input
              type="number"
              step="any"
              placeholder={fmtPrice(market.markPrice)}
              value={price}
              onChange={e => setPrice(e.target.value)}
              className={`w-full text-sm px-3 py-2 rounded-lg border outline-none font-mono ${t.inp}`}
            />
          </div>
        )}

        {/* Size */}
        <div className="mb-5">
          <label className={`text-xs ${t.muted} mb-1 block`}>Miktar (kontrat)</label>
          <input
            type="number"
            step="any"
            placeholder="0.001"
            value={size}
            onChange={e => setSize(e.target.value)}
            className={`w-full text-sm px-3 py-2 rounded-lg border outline-none font-mono ${t.inp}`}
          />
          {size && market.markPrice > 0 && (
            <p className={`text-[11px] ${t.muted} mt-1`}>
              ≈ ${(parseFloat(size) * market.markPrice || 0).toFixed(2)} USDC
            </p>
          )}
        </div>

        {/* Current funding info */}
        <div className={`rounded-lg p-3 mb-4 text-xs ${dark ? 'bg-[#0a2560]/40' : 'bg-blue-50'} border ${t.cardBorder}`}>
          <div className="flex justify-between">
            <span className={t.muted}>Son Funding</span>
            <span className={`font-mono font-semibold ${market.fundingRate >= 0 ? t.positive : t.negative}`}>
              {fmtFunding(market.fundingRate)}
            </span>
          </div>
          <div className="flex justify-between mt-1">
            <span className={t.muted}>Tahmini Funding</span>
            <span className={`font-mono font-semibold ${market.predictedFundingRate >= 0 ? t.positive : t.negative}`}>
              {fmtFunding(market.predictedFundingRate)}
            </span>
          </div>
        </div>

        {/* Submit */}
        <button
          onClick={handleSubmit}
          disabled={!isValid || busy}
          className={`w-full py-2.5 rounded-xl text-sm font-bold transition-all ${
            side === 'LONG'
              ? 'bg-emerald-600 hover:bg-emerald-500 text-white'
              : 'bg-rose-600 hover:bg-rose-500 text-white'
          } disabled:opacity-40 disabled:cursor-not-allowed`}
        >
          {busy ? 'Gönderiliyor...' : `${side} — ${orderType}`}
        </button>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function DecibelTrade({ dark = true }: Props) {
  const t = getTheme(dark);

  const [markets,    setMarkets]   = useState<DecibelMarket[]>([]);
  const [account,    setAccount]   = useState<DecibelAccount | null>(null);
  const [positions,  setPositions] = useState<DecibelPosition[]>([]);
  const [openOrders, setOpenOrders] = useState<DecibelOrder[]>([]);
  const [connected,  setConnected] = useState(false);
  const [lastUpdate, setLastUpdate] = useState('—');
  const [now,        setNow]       = useState(Date.now());

  const [favorites,    setFavs]       = useState<string[]>(loadFavs);
  const [search,       setSearch]     = useState('');
  const [sortKey,      setSortKey]    = useState<SortKey>('volume');
  const [sortDir,      setSortDir]    = useState<SortDir>('desc');
  const [showFavsOnly, setShowFavsOnly] = useState(false);

  // Order modal
  const [selectedMarket, setSelectedMarket] = useState<DecibelMarket | null>(null);
  const [orderBusy,      setOrderBusy]      = useState(false);
  const [orderResult,    setOrderResult]    = useState<{ ok: boolean; msg: string } | null>(null);

  const wsRef = useRef<WebSocket | null>(null);

  // WebSocket
  useEffect(() => {
    let dead = false;
    let retry: ReturnType<typeof setTimeout>;
    function connect() {
      if (dead) return;
      const ws = new WebSocket(DECIBEL_WS);
      wsRef.current = ws;
      ws.onopen = () => {
        setConnected(true);
        ws.send(JSON.stringify({ type: 'decibel_get_state' }));
      };
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          if (msg.type === 'decibel_markets' && Array.isArray(msg.payload)) {
            setMarkets(msg.payload as DecibelMarket[]);
            setLastUpdate(new Date().toLocaleTimeString('tr-TR'));
          } else if (msg.type === 'decibel_account' && msg.payload) {
            const pl = msg.payload;
            if (pl.account)     setAccount(pl.account as DecibelAccount);
            if (pl.positions)   setPositions(pl.positions as DecibelPosition[]);
            if (pl.open_orders) setOpenOrders(pl.open_orders as DecibelOrder[]);
          } else if (msg.type === 'decibel_order_result') {
            setOrderBusy(false);
            const ok = msg.payload?.ok;
            setOrderResult({ ok, msg: ok ? 'İşlem gönderildi ✓' : (msg.payload?.error || 'Hata') });
            setSelectedMarket(null);
          } else if (msg.type === 'decibel_cancel_result') {
            const ok = msg.payload?.ok;
            setOrderResult({ ok, msg: ok ? 'İptal edildi ✓' : (msg.payload?.error || 'Hata') });
          }
        } catch {}
      };
      ws.onclose = () => { setConnected(false); if (!dead) retry = setTimeout(connect, 3000); };
      ws.onerror = () => ws.close();
    }
    connect();
    return () => { dead = true; clearTimeout(retry); wsRef.current?.close(); };
  }, []);

  // Live countdown timer
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // Auto-dismiss result toast
  useEffect(() => {
    if (!orderResult) return;
    const id = setTimeout(() => setOrderResult(null), 5000);
    return () => clearTimeout(id);
  }, [orderResult]);

  const toggleFav = (sym: string) => {
    setFavs(prev => {
      const next = prev.includes(sym) ? prev.filter(s => s !== sym) : [...prev, sym];
      localStorage.setItem(FAVS_KEY, JSON.stringify(next));
      return next;
    });
  };

  const cycleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  const sendWs = (msg: object) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(msg));
    }
  };

  const handleOrderSubmit = (order: any) => {
    setOrderBusy(true);
    sendWs({ type: 'decibel_place_order', payload: order });
  };

  const handleCancelOrder = (orderId: string) => {
    sendWs({ type: 'decibel_cancel_order', payload: { order_id: orderId } });
  };

  const handleClosePosition = (pos: DecibelPosition) => {
    sendWs({ type: 'decibel_close_position', payload: { symbol: pos.symbol, side: pos.side, size: pos.size } });
  };

  // Sorting + filtering
  const filtered = markets
    .filter(m => {
      if (showFavsOnly && !favorites.includes(m.symbol)) return false;
      const q = search.toLowerCase();
      return !q || m.symbol.toLowerCase().includes(q) || shortSymbol(m.symbol).toLowerCase().includes(q);
    })
    .sort((a, b) => {
      if (sortKey === 'alpha') {
        return sortDir === 'asc'
          ? a.symbol.localeCompare(b.symbol)
          : b.symbol.localeCompare(a.symbol);
      }
      let va = 0, vb = 0;
      if (sortKey === 'volume')   { va = a.volume24h;           vb = b.volume24h; }
      if (sortKey === 'change')   { va = a.priceChange24h;      vb = b.priceChange24h; }
      if (sortKey === 'funding')  { va = a.fundingRate;         vb = b.fundingRate; }
      if (sortKey === 'pfunding') { va = a.predictedFundingRate; vb = b.predictedFundingRate; }
      if (sortKey === 'oi')       { va = a.openInterestUsd || a.openInterest * a.markPrice; vb = b.openInterestUsd || b.openInterest * b.markPrice; }
      return sortDir === 'desc' ? vb - va : va - vb;
    });

  const sorted = [
    ...filtered.filter(m => favorites.includes(m.symbol)),
    ...filtered.filter(m => !favorites.includes(m.symbol)),
  ];

  const SortBtn = ({ k, label }: { k: SortKey; label: string }) => (
    <button onClick={() => cycleSort(k)}
      className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors ${
        sortKey === k ? 'bg-[#4A9FE8] text-[#061530] font-bold' : `${t.btnSecondary} opacity-70`
      }`}>
      {label}{sortKey === k && <ArrowUpDown size={10} />}
    </button>
  );

  const card = `${t.card} border ${t.cardBorder} rounded-xl`;
  const hasAccount = account && (account.equity > 0 || account.accountValue > 0);

  return (
    <div className="p-6 space-y-4">

      {/* Toast */}
      {orderResult && (
        <div className={`fixed top-4 right-4 z-50 px-5 py-3 rounded-xl shadow-2xl text-sm font-semibold flex items-center gap-2 transition-all ${
          orderResult.ok ? 'bg-emerald-600 text-white' : 'bg-rose-600 text-white'
        }`}>
          {orderResult.ok ? <Zap size={16} /> : <AlertCircle size={16} />}
          {orderResult.msg}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className={`text-xl font-bold ${t.text}`}>Decibel Trade — Aptos Perps</h2>
          <p className={`text-xs mt-0.5 ${t.muted}`}>
            Aptos L1 on-chain CLOB · {markets.length} market · 8s funding periyot
          </p>
        </div>
        <div className={`flex items-center gap-2 text-xs ${t.muted}`}>
          <div className={`w-2 h-2 rounded-full ${!connected ? 'bg-rose-400' : markets.length === 0 ? 'bg-yellow-400 animate-pulse' : 'bg-green-400'}`} />
          {!connected ? 'Backend bekleniyor...' : markets.length === 0 ? 'Decibel verisi bekleniyor...' : `Son: ${lastUpdate}`}
        </div>
      </div>

      {/* Account summary */}
      {hasAccount && (
        <div className="grid grid-cols-4 gap-3">
          {[
            { label: 'Hesap Değeri',  value: `$${account.accountValue.toFixed(2)}`,  pos: null },
            { label: 'Equity',        value: `$${account.equity.toFixed(2)}`,         pos: null },
            { label: 'Serbest Marjin', value: `$${account.freeMargin.toFixed(2)}`,   pos: null },
            {
              label: 'Gerçekleşmemiş PnL',
              value: `${account.unrealizedPnl >= 0 ? '+' : ''}$${account.unrealizedPnl.toFixed(2)}`,
              pos: account.unrealizedPnl >= 0,
            },
          ].map(c => (
            <div key={c.label} className={`${card} p-4`}>
              <p className={`text-xs mb-1 ${t.muted}`}>{c.label}</p>
              <p className={`text-base font-bold font-mono ${
                c.pos === null ? t.text : c.pos ? t.positive : t.negative
              }`}>{c.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Positions */}
      {positions.length > 0 && (
        <div className={`${card} overflow-hidden`}>
          <div className={`px-4 py-2.5 border-b ${t.cardBorder}`}>
            <h3 className={`text-sm font-semibold ${t.text}`}>Açık Pozisyonlar ({positions.length})</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className={`${t.tableHeader} border-b`}>
                  {['Sembol', 'Yön', 'Miktar', 'Giriş', 'Mark', 'Liq.', 'PnL', 'Kaldıraç', ''].map(h => (
                    <th key={h} className={`px-3 py-2 ${h === '' ? '' : 'text-left'}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {positions.map((pos, i) => {
                  const pnlPct = pos.entryPrice > 0
                    ? ((pos.markPrice - pos.entryPrice) / pos.entryPrice * 100 * (pos.side === 'LONG' ? 1 : -1))
                    : 0;
                  return (
                    <tr key={i} className={`border-b ${t.tableRow}`}>
                      <td className={`px-3 py-2 font-bold ${t.gold}`}>{pos.symbol}</td>
                      <td className={`px-3 py-2 font-semibold ${pos.side === 'LONG' ? t.positive : t.negative}`}>
                        {pos.side === 'LONG' ? '▲ Long' : '▼ Short'}
                      </td>
                      <td className={`px-3 py-2 font-mono ${t.text}`}>{pos.size}</td>
                      <td className={`px-3 py-2 font-mono ${t.muted}`}>${fmtPrice(pos.entryPrice)}</td>
                      <td className={`px-3 py-2 font-mono ${t.text}`}>${fmtPrice(pos.markPrice)}</td>
                      <td className={`px-3 py-2 font-mono text-rose-400`}>
                        {pos.liquidationPrice > 0 ? `$${fmtPrice(pos.liquidationPrice)}` : '—'}
                      </td>
                      <td className={`px-3 py-2 font-mono font-bold ${pos.unrealizedPnl >= 0 ? t.positive : t.negative}`}>
                        {pos.unrealizedPnl >= 0 ? '+' : ''}${pos.unrealizedPnl.toFixed(2)}
                        <span className={`ml-1 text-[10px] ${pnlPct >= 0 ? t.positive : t.negative}`}>
                          ({pnlPct >= 0 ? '+' : ''}{pnlPct.toFixed(2)}%)
                        </span>
                      </td>
                      <td className={`px-3 py-2 font-mono ${t.muted}`}>{pos.leverage.toFixed(1)}x</td>
                      <td className="px-3 py-2 text-right">
                        <button
                          onClick={() => handleClosePosition(pos)}
                          className="text-xs px-2 py-1 rounded bg-rose-600/20 text-rose-400 hover:bg-rose-600/40 transition-colors font-semibold"
                        >
                          Kapat
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Open orders */}
      {openOrders.length > 0 && (
        <div className={`${card} overflow-hidden`}>
          <div className={`px-4 py-2.5 border-b ${t.cardBorder}`}>
            <h3 className={`text-sm font-semibold ${t.text}`}>Bekleyen Emirler ({openOrders.length})</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className={`${t.tableHeader} border-b`}>
                  {['Sembol', 'Yön', 'Tür', 'Fiyat', 'Miktar', 'Doldu', 'Durum', ''].map(h => (
                    <th key={h} className={`px-3 py-2 text-left`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {openOrders.map((o, i) => (
                  <tr key={i} className={`border-b ${t.tableRow}`}>
                    <td className={`px-3 py-2 font-bold ${t.gold}`}>{o.symbol}</td>
                    <td className={`px-3 py-2 ${o.side === 'LONG' ? t.positive : t.negative} font-semibold`}>
                      {o.side}
                    </td>
                    <td className={`px-3 py-2 ${t.muted}`}>{o.orderType}</td>
                    <td className={`px-3 py-2 font-mono ${t.text}`}>
                      {o.price > 0 ? `$${fmtPrice(o.price)}` : 'MARKET'}
                    </td>
                    <td className={`px-3 py-2 font-mono ${t.text}`}>{o.size}</td>
                    <td className={`px-3 py-2 font-mono ${t.muted}`}>{o.filledSize}</td>
                    <td className={`px-3 py-2 ${t.muted}`}>{o.status}</td>
                    <td className="px-3 py-2">
                      <button
                        onClick={() => handleCancelOrder(o.orderId)}
                        className="text-xs px-2 py-1 rounded bg-rose-600/20 text-rose-400 hover:bg-rose-600/40 transition-colors font-semibold"
                      >
                        İptal
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Market stats summary */}
      {markets.length > 0 && (
        <div className="grid grid-cols-4 gap-3">
          {[
            {
              label: 'Ort. Funding',
              value: fmtFunding(markets.reduce((s, m) => s + m.fundingRate, 0) / markets.length),
              pos: markets.reduce((s, m) => s + m.fundingRate, 0) >= 0,
            },
            {
              label: 'En Yüksek Funding',
              value: fmtFunding(Math.max(...markets.map(m => m.fundingRate))),
              pos: true,
            },
            {
              label: 'En Düşük Funding',
              value: fmtFunding(Math.min(...markets.map(m => m.fundingRate))),
              pos: Math.min(...markets.map(m => m.fundingRate)) >= 0,
            },
            { label: 'Aktif Market', value: String(markets.length), pos: null },
          ].map(c => (
            <div key={c.label} className={`${card} p-4`}>
              <p className={`text-xs mb-1 ${t.muted}`}>{c.label}</p>
              <p className={`text-base font-bold font-mono ${
                c.pos === null ? t.text : c.pos ? t.positive : t.negative
              }`}>{c.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Controls */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border ${
          dark ? 'bg-[#0a2560]/60 border-[#003087]' : 'bg-white border-blue-300'
        }`}>
          <Search size={13} className={t.muted} />
          <input
            placeholder="Market ara..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className={`bg-transparent text-xs outline-none w-28 ${t.text}`}
          />
        </div>

        <button onClick={() => setShowFavsOnly(v => !v)}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
            showFavsOnly ? 'bg-[#4A9FE8] text-[#061530] font-bold' : t.btnSecondary
          }`}>
          <Star size={12} fill={showFavsOnly ? 'currentColor' : 'none'} />
          Favoriler {favorites.length > 0 && `(${favorites.length})`}
        </button>

        <div className={`h-4 w-px ${dark ? 'bg-[#0a2560]' : 'bg-blue-200'}`} />

        <span className={`text-xs ${t.muted}`}>Sırala:</span>
        <SortBtn k="volume"   label="Hacim" />
        <SortBtn k="alpha"    label="A-Z" />
        <SortBtn k="change"   label="24s %" />
        <SortBtn k="funding"  label="Funding" />
        <SortBtn k="pfunding" label="Tah. Funding" />
        <SortBtn k="oi"       label="OI" />
      </div>

      {/* Markets table */}
      <div className={`${card} overflow-hidden`}>
        {markets.length === 0 ? (
          <div className={`p-10 text-center ${t.muted} text-sm`}>
            {connected
              ? 'Decibel verisi bekleniyor... (backend her 5s yayınlar)'
              : 'Backend bağlantısı bekleniyor...'}
          </div>
        ) : sorted.length === 0 ? (
          <div className={`p-10 text-center ${t.muted} text-sm`}>
            {showFavsOnly ? 'Favori market eklenmedi.' : 'Eşleşen market bulunamadı.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className={`${t.tableHeader} border-b`}>
                  <th className="px-3 py-2.5 w-8"></th>
                  <th className="px-3 py-2.5 text-left">Market</th>
                  <th className={`px-3 py-2.5 text-right ${dark ? 'text-emerald-400' : 'text-emerald-600'}`}>Bid</th>
                  <th className={`px-3 py-2.5 text-right ${dark ? 'text-rose-400' : 'text-rose-600'}`}>Ask</th>
                  <th className="px-3 py-2.5 text-right">Mark</th>
                  <th className="px-3 py-2.5 text-right">Index</th>
                  <th className="px-3 py-2.5 text-right">24s %</th>
                  <th className={`px-3 py-2.5 text-right font-bold ${dark ? 'text-cyan-300' : 'text-cyan-700'}`}>Funding</th>
                  <th className={`px-3 py-2.5 text-right font-bold ${dark ? 'text-yellow-300' : 'text-yellow-700'}`}>Tah. Funding</th>
                  <th className="px-3 py-2.5 text-right">Sonraki</th>
                  <th className="px-3 py-2.5 text-right">OI ($M)</th>
                  <th className="px-3 py-2.5 text-right">24s Hacim</th>
                  <th className="px-3 py-2.5 text-right"></th>
                </tr>
              </thead>
              <tbody>
                {sorted.map(m => {
                  const isFav = favorites.includes(m.symbol);
                  const highFunding = Math.abs(m.predictedFundingRate) >= 0.0005;
                  const msLeft = m.nextFundingTime > 0
                    ? Math.max(0, m.nextFundingTime - now)
                    : 0;
                  const spread = m.bestBid > 0 && m.bestAsk > 0 && m.markPrice > 0
                    ? ((m.bestAsk - m.bestBid) / m.markPrice) * 100
                    : 0;

                  return (
                    <tr key={m.symbol} className={`border-b ${t.tableRow} ${isFav ? (dark ? 'bg-[#4A9FE8]/5' : 'bg-yellow-50/60') : ''}`}>

                      {/* Fav */}
                      <td className="px-3 py-2 text-center">
                        <button onClick={() => toggleFav(m.symbol)}
                          className={`transition-colors ${isFav ? 'text-[#4A9FE8]' : `${t.muted} hover:text-[#4A9FE8]`}`}>
                          <Star size={13} fill={isFav ? 'currentColor' : 'none'} />
                        </button>
                      </td>

                      {/* Symbol */}
                      <td className="px-3 py-2">
                        <span className={`font-bold ${t.gold}`}>{shortSymbol(m.symbol)}</span>
                        <span className={`ml-1.5 text-[10px] ${t.muted} opacity-60`}>{m.symbol}</span>
                        {isFav && <span className="ml-1 text-[10px] text-[#4A9FE8] opacity-60">★</span>}
                      </td>

                      {/* Bid */}
                      <td className={`px-3 py-2 text-right font-mono ${t.positive}`}>
                        {m.bestBid > 0 ? `$${fmtPrice(m.bestBid)}` : '—'}
                      </td>

                      {/* Ask */}
                      <td className={`px-3 py-2 text-right font-mono ${t.negative}`}>
                        {m.bestAsk > 0 ? `$${fmtPrice(m.bestAsk)}` : '—'}
                      </td>

                      {/* Mark */}
                      <td className={`px-3 py-2 text-right font-mono font-semibold ${t.text}`}>
                        ${fmtPrice(m.markPrice)}
                      </td>

                      {/* Index */}
                      <td className={`px-3 py-2 text-right font-mono ${t.muted}`}>
                        {m.indexPrice > 0 ? `$${fmtPrice(m.indexPrice)}` : '—'}
                      </td>

                      {/* 24h change */}
                      <td className={`px-3 py-2 text-right font-mono font-bold ${m.priceChange24h >= 0 ? t.positive : t.negative}`}>
                        <span className="flex items-center justify-end gap-0.5">
                          {m.priceChange24h >= 0 ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
                          {m.priceChange24h >= 0 ? '+' : ''}{(m.priceChange24h * 100).toFixed(2)}%
                        </span>
                      </td>

                      {/* Funding */}
                      <td className={`px-3 py-2 text-right font-mono font-semibold ${m.fundingRate >= 0 ? t.positive : t.negative}`}>
                        {fmtFunding(m.fundingRate)}
                      </td>

                      {/* Predicted funding */}
                      <td className={`px-3 py-2 text-right font-mono font-bold ${
                        highFunding
                          ? m.predictedFundingRate >= 0
                            ? 'text-emerald-300 bg-emerald-900/20'
                            : 'text-rose-300 bg-rose-900/20'
                          : m.predictedFundingRate >= 0 ? t.positive : t.negative
                      }`}>
                        {fmtFunding(m.predictedFundingRate)}
                        {highFunding && <span className="ml-1 text-[9px] opacity-70">▲</span>}
                      </td>

                      {/* Countdown */}
                      <td className={`px-3 py-2 text-right font-mono ${t.muted}`}>
                        {msLeft > 0 ? (
                          <span className="flex items-center justify-end gap-1">
                            <Clock size={10} />
                            {formatCountdown(msLeft)}
                          </span>
                        ) : '—'}
                      </td>

                      {/* OI */}
                      <td className={`px-3 py-2 text-right ${t.muted}`}>
                        {m.openInterestUsd > 0
                          ? `$${(m.openInterestUsd / 1e6).toFixed(2)}M`
                          : m.openInterest > 0 && m.markPrice > 0
                            ? `$${(m.openInterest * m.markPrice / 1e6).toFixed(2)}M`
                            : '—'}
                      </td>

                      {/* Volume */}
                      <td className={`px-3 py-2 text-right font-semibold ${t.text}`}>
                        {m.volume24h > 0 ? `$${(m.volume24h / 1e6).toFixed(2)}M` : '—'}
                      </td>

                      {/* Trade button */}
                      <td className="px-3 py-2 text-right">
                        <button
                          onClick={() => { setSelectedMarket(m); setOrderResult(null); }}
                          className="text-xs px-2 py-1 rounded bg-[#4A9FE8]/20 text-[#4A9FE8] hover:bg-[#4A9FE8]/40 transition-colors font-semibold"
                        >
                          İşlem
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <p className={`text-[10px] ${t.muted} text-right`}>
        Kaynak: Decibel Trade API (Aptos L1) · Backend her 5s yeniler
      </p>

      {/* Order modal */}
      {selectedMarket && (
        <OrderModal
          market={selectedMarket}
          dark={dark}
          onClose={() => setSelectedMarket(null)}
          onSubmit={handleOrderSubmit}
          busy={orderBusy}
        />
      )}
    </div>
  );
}
